/**
 * Where each node of the force graph was, by id, so a rebuild of the graph (a click that changes the scope, the
 * scrubber, the hops, the 30 s clock, a resize) starts the nodes that stay where they were instead of from nothing.
 * Pure: no d3, no DOM. The diagram owns the memory and calls these before it starts its simulation.
 */

export interface PlacedNode {
  id: string;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

export interface LinkEnds {
  source: string | number | { id: string };
  target: string | number | { id: string };
}

export interface RememberedPlace {
  x: number;
  y: number;
  /** Set when the person dragged the node: it stays pinned until they release it. */
  fx?: number | null;
  fy?: number | null;
  /** True once the simulation that placed it had come to rest. Only a place that is at rest is held fixed on a rebuild. */
  settled?: boolean;
}

export type PlaceMemory = Map<string, RememberedPlace>;

/** The most nodes the memory holds; the oldest go first. A tenant's whole graph is far below this. */
export const PLACE_MEMORY_LIMIT = 2000;

/** The share of the nodes on screen before that must stay for a change to count as an update, whose nodes are held. */
const UPDATE_SHARE = 0.5;

/** How far from its neighbour a new node is first placed, in graph pixels. */
const NEIGHBOUR_OFFSET = 36;

const endId = (end: LinkEnds['source']): string => (typeof end === 'object' ? end.id : String(end));

/** A stable angle from the id, so the same new node lands on the same side every time. */
function angleOf(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return ((hash % 360) * Math.PI) / 180;
}

/**
 * Puts every node that has a remembered place back there (pinned ones stay pinned), then places each new node beside
 * the neighbours that already have one, and the rest at `fallback`.
 *
 * A kept node whose place was at rest is also held fixed (`held`: the caller lets it go when the new nodes have
 * settled), so the nodes that stay do not move at all while the new ones find their place around them. A kept node
 * whose place was not at rest yet (the graph was redrawn while it was still settling) is left free to go on settling.
 *
 * Holding is only worth it for an update, where most of what was on screen stays on screen (a refresh, the hops, a
 * neighbouring incident, a wider scope). When `previousCount` is given and fewer than half of those nodes are kept (a
 * big graph narrowed to a handful of nodes that were far apart), nothing is held: the kept nodes count as unsettled and
 * the layout flows from where they were to a compact one, and the places of the nodes that left are forgotten, since the
 * ones that stayed no longer share a layout with them. `reflow` says so (and is true for a first drawing), so the caller
 * can fit the new layout in the canvas.
 */
export function placeFromMemory<N extends PlacedNode>(
  nodes: N[],
  links: LinkEnds[],
  memory: PlaceMemory,
  fallback: { x: number; y: number },
  previousCount?: number
): { kept: number; added: number; unsettled: number; held: Set<string>; reflow: boolean } {
  const placed = new Set<string>();
  const held = new Set<string>();
  const holdable: N[] = [];
  let unsettled = 0;
  for (const node of nodes) {
    const spot = memory.get(node.id);
    if (!spot) continue;
    node.x = spot.x;
    node.y = spot.y;
    node.fx = spot.fx ?? null;
    node.fy = spot.fy ?? null;
    if (spot.settled) {
      if (node.fx == null && node.fy == null) holdable.push(node);
    } else {
      unsettled += 1;
    }
    placed.add(node.id);
  }
  const kept = placed.size;
  const update = previousCount === undefined || kept >= previousCount * UPDATE_SHARE;
  if (update) {
    for (const node of holdable) {
      node.fx = node.x;
      node.fy = node.y;
      held.add(node.id);
    }
  } else {
    unsettled += holdable.length;
    const staying = new Set(nodes.map((node) => node.id));
    Array.from(memory.keys()).forEach((id) => {
      if (!staying.has(id)) memory.delete(id);
    });
  }
  if (kept === 0) return { kept: 0, added: nodes.length, unsettled: 0, held, reflow: true };

  const neighbours = new Map<string, string[]>();
  for (const link of links) {
    const a = endId(link.source);
    const b = endId(link.target);
    if (!neighbours.has(a)) neighbours.set(a, []);
    if (!neighbours.has(b)) neighbours.set(b, []);
    neighbours.get(a)!.push(b);
    neighbours.get(b)!.push(a);
  }
  const byId = new Map(nodes.map((node) => [node.id, node]));

  // Two passes, so a new node whose only neighbour is itself new still finds a place.
  for (let pass = 0; pass < 2; pass += 1) {
    for (const node of nodes) {
      if (placed.has(node.id)) continue;
      const near = (neighbours.get(node.id) || [])
        .filter((id) => placed.has(id))
        .map((id) => byId.get(id)!);
      if (near.length === 0) continue;
      const cx = near.reduce((sum, n) => sum + (n.x ?? 0), 0) / near.length;
      const cy = near.reduce((sum, n) => sum + (n.y ?? 0), 0) / near.length;
      const angle = angleOf(node.id);
      node.x = cx + NEIGHBOUR_OFFSET * Math.cos(angle);
      node.y = cy + NEIGHBOUR_OFFSET * Math.sin(angle);
      placed.add(node.id);
    }
  }
  for (const node of nodes) {
    if (placed.has(node.id)) continue;
    const angle = angleOf(node.id);
    node.x = fallback.x + NEIGHBOUR_OFFSET * Math.cos(angle);
    node.y = fallback.y + NEIGHBOUR_OFFSET * Math.sin(angle);
  }
  return { kept, added: nodes.length - kept, unsettled, held, reflow: !update };
}

/**
 * The energy a simulation restarts with. Nothing remembered is a first drawing (full energy). Everything remembered needs
 * only a trickle to settle; each new node adds some. If some kept places had not come to rest yet, the layout is still
 * unfinished and needs more than a trickle.
 */
export function restartAlpha(kept: number, added: number, unsettled = 0): number {
  if (kept === 0) return 1;
  const base = added === 0 ? 0.05 : Math.min(0.5, 0.1 + (0.5 * added) / (kept + added));
  return unsettled > 0 ? Math.max(base, 0.5) : base;
}

/**
 * Writes the nodes' current places (and pins) into the memory, keeping it within its limit. `settled`: the simulation was
 * at rest. A `held` node is fixed only for the length of one simulation, so it is stored free, never as a pin of the person,
 * and still at rest: it has not moved from the place that was at rest, even when the graph is redrawn twice in a row
 * (a new scope redraws it once for the data and once for the scrubber) before its simulation has run.
 */
export function rememberPlaces(
  memory: PlaceMemory,
  nodes: PlacedNode[],
  settled = false,
  held?: Set<string>
): void {
  for (const node of nodes) {
    if (node.x == null || node.y == null || Number.isNaN(node.x) || Number.isNaN(node.y)) continue;
    const free = held?.has(node.id);
    memory.delete(node.id);
    memory.set(node.id, {
      x: node.x,
      y: node.y,
      fx: free ? null : node.fx ?? null,
      fy: free ? null : node.fy ?? null,
      settled: free ? true : settled,
    });
  }
  while (memory.size > PLACE_MEMORY_LIMIT) {
    const oldest = memory.keys().next().value;
    if (oldest === undefined) break;
    memory.delete(oldest);
  }
}

/**
 * Moves every remembered place by the same amount (pins included). A resize moves the middle of the canvas by half of
 * what the canvas grew, and the graph must follow it as one piece, not be redrawn.
 */
export function shiftPlaces(memory: PlaceMemory, dx: number, dy: number): void {
  if (dx === 0 && dy === 0) return;
  memory.forEach((spot) => {
    spot.x += dx;
    spot.y += dy;
    if (spot.fx != null) spot.fx += dx;
    if (spot.fy != null) spot.fy += dy;
  });
}
